import { useWizard } from 'react-use-wizard';
import Textarea from '@ramsey-design-system/textarea';
import { FieldErrors, useForm } from 'react-hook-form';
import { useGAFormTracking } from '../../../../../shared/hooks';
import { useState } from 'react';
import { ReferralFormData, useReferralContext } from '../../../context';
import {
  getFormName,
  useTrackStepError,
  useTrackStepStarted
} from '../../../utils';
import FormField from '@ramsey-design-system/form-field';
import { trackStepCompleted } from '../../../../../shared/analytics';
import {
  StandardStepSubtitleText,
  StandardStepTitle,
  StepWrapper
} from '../../stepComponents';
import { StandardNextButton } from '../../buttons';
import { generateSensitiveDataSchema } from '../../../../../shared/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

const STEP_NAME = 'questions_step';
const STEP_LABEL = 'What questions do you have for your tax pro?';

interface QuestionsStepFields {
  tax_questions: string;
}

function QuestionsStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  const { nextStep, activeStep } = useWizard();
  const [text, setText] = useState('');
  const { referral, updateReferral } = useReferralContext();
  const schema = generateSensitiveDataSchema(
    'tax_questions'
  ) as unknown as Yup.ObjectSchema<QuestionsStepFields>;
  const { register, handleSubmit, formState } = useForm<QuestionsStepFields>({
    defaultValues: {
      ...referral
    },
    resolver: yupResolver(schema)
  });
  const errorMessage = formState.errors.tax_questions?.message;

  useTrackStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);
  const trackError = useTrackStepError(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function onValidSubmit(referral: Partial<ReferralFormData>) {
    updateReferral(referral);
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: ['What questions do you have for your tax pro?'],
      answer_values: [referral.tax_questions!],
      form_name: getFormName()
    });
    await nextStep();
  }

  function onInvalid(formErrors: FieldErrors<QuestionsStepFields>) {
    trackError(
      'validation',
      formErrors.tax_questions?.message,
      'tax_questions'
    );
  }

  return (
    <StepWrapper>
      <form ref={formRef} onSubmit={handleSubmit(onValidSubmit, onInvalid)}>
        <StandardStepTitle>
          What questions do you have for your tax pro?
        </StandardStepTitle>
        <StandardStepSubtitleText>Optional</StandardStepSubtitleText>

        <FormField id="question_box" size="lg">
          {() => (
            <div>
              <Textarea
                {...register('tax_questions')}
                size="lg"
                maxLength={250}
                id="tax_questions_textbox"
                aria-describedby="tax_questions_textbox"
                data-testid="tax_questions_textbox"
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            </div>
          )}
        </FormField>
        {(errorMessage && (
          <p className="text-2 text-red-60 pt-1 pb-4">{errorMessage}</p>
        )) || <p className="pb-4"></p>}
        <StandardNextButton />
      </form>
    </StepWrapper>
  );
}

export { QuestionsStep };
